import * as React from 'react';
import { useState } from 'react';
import { CommonPopover } from '../common-popover/common-popover';
import { CommonPopoverContext } from '../common-popover/common-popover.context';
import { SearchInput } from '../search-input/search-input';
import { Component } from '../../../infrastructure/type-extensions';
import './make-multi-select.scss';
import { MultiSelectList } from '../multi-select-list/multi-select-list';
import { DefaultClosePopoverButton } from '../common-popover/default-close-popover-button';
import { FormattedMessage } from 'react-intl';
import { LOADING, applyText } from '../../../constants/common-labels.constants';

enum MultiSelectFilterType {
    Group,
    Item
}
interface IMultiSelectClosedPropsDerivedFromItemsList {
    isOpen: boolean;
}

const defaultHeader = ({ searchInput }) => searchInput;

const makeMultiSelectInPopover = <T extends IMultiSelectClosedPropsDerivedFromItemsList>(ComponentWhichTriggerSingleSelection: Component<T>) => {
    // we need to utilize context.consumer in order to identify 'isOpen' value, but usage of popover forcing ref forwarding
    // therefore utilize React.forwardRef to create HOC over the ComponentWhichTriggerSingleSelection, which would utilize context and forward ref
    const ComponentWithForwardedRef = React.forwardRef<HTMLElement, T>((props: T, ref: React.Ref<HTMLElement>) => {
        return (
            <CommonPopoverContext.Consumer>
                {context => <ComponentWhichTriggerSingleSelection {...props} isOpen={context.isOpen} ref={ref} />}
            </CommonPopoverContext.Consumer>
        );
    });

    return props => {
        const [filterCriteria, setFilterCriteria] = useState<string>('');
        // store currently selected values and do not propagate them to outer worlds until popup is closed
        const [selectedValues, setSelectedValues] = useState(props.value);
        const [validationError, setValidationError] = useState<string>('');
        const formatSearchValue = (value: string): string =>
        value.trim().toUpperCase();

        let filteredItems = [];
        const formattedSearchCriteria = formatSearchValue(filterCriteria);

        if (!filterCriteria) {
            filteredItems = props.items;
        } else {
             filteredItems = props.items.filter(x =>
            formatSearchValue(x[props.nameProperty].toString()).includes(formattedSearchCriteria));
        }
        const shallRenderLoader = !props.items;
        let shallRenderNoItems = false;
        if (!shallRenderLoader) {
            shallRenderNoItems = Array.isArray(props.items)
                ? props.items.length === 0
                : Object.keys(props.items).length === 0;
        }

        const shallRenderEmptySearch = !shallRenderLoader && !shallRenderNoItems && Object.keys(filteredItems).length === 0;
        const shallRenderMultiSelect = !shallRenderLoader && !shallRenderNoItems && !shallRenderEmptySearch;

        const applyButtonText = props.applyButtonText || applyText();
        ;
        const resetButtonText = props.resetButtonText || <FormattedMessage
            id='oe.common.reset'
            description='Button text'
            defaultMessage='Reset'
        />;

        React.useEffect(() => {
            if (props.value) setSelectedValues(props.value);
        }, [props.value]);

        React.useEffect(() => {
            if (validationError && selectedValues && selectedValues.length > 0) {
                setValidationError('');
            }
        }, [selectedValues]);

        const { renderHeader = defaultHeader, withSearch = true } = props;

        // T can not be casted to ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>
        // can't get the reason for this, but for now just cast targetComponentProps to any while passing to component with forwarded ref
        /* eslint-disable @typescript-eslint/no-explicit-any */
        return (
            <CommonPopover
                className={props.popoverClassName || ''}
                header={
                    renderHeader( {
                         searchInput: withSearch ? (
                            <SearchInput
                                placeholder={props.searchPlaceholder || 'Search'}
                                onChange={e => {
                                    props.filterHandler ?
                                        props.filterHandler(e.target.value) :
                                        setFilterCriteria(e.target.value);
                                }}
                                className='w-100'
                                value={filterCriteria}
                                autoFocus={true}
                                withDebounce={props.searchWithDebounce}
                            />
                        ) : <React.Fragment/>
                    })
                }
                body={(
                    <>
                        { shallRenderLoader && (
                            <div className='single-select-no-content multi-select-loader'>
                                <FormattedMessage
                                    {...LOADING}
                                    values={{
                                        suffix: '...'
                                    }}
                                />
                            </div>
                        )}
                        { shallRenderNoItems && <div className='single-select-no-content'>{props.emptyListText || <FormattedMessage
                            id='oe.items.no'
                            description='Label text'
                            defaultMessage='No items'
                        />}</div>}
                        { shallRenderEmptySearch &&
                            <div className='single-select-no-content'>{props.emptyFilterText || <FormattedMessage
                                id='oe.items.noMatchingSearchCriteria'
                                description='Label text'
                                defaultMessage='No items matching search criteria'
                            />}</div>}
                        { shallRenderMultiSelect && (!props.renderSelectList ? (
                            <MultiSelectList
                                keyProperty={props.keyProperty}
                                nameProperty={props.nameProperty}
                                disabled={props.disabled}
                                value={selectedValues}
                                items={filteredItems}
                                onChange={checkedOptions => setSelectedValues(checkedOptions)}
                            />
                        ) : (
                            props.renderSelectList({
                                keyProperty: props.keyProperty,
                                nameProperty: props.nameProperty,
                                disabled: props.disabled,
                                value: selectedValues,
                                items: filteredItems,
                                onChange: checkedOptions => setSelectedValues(checkedOptions),
                            })
                        ))}
                        {validationError && (
                            <div className='oe-form-field__error m-10px'>
                                {validationError}
                            </div>
                        )}
                    </>
                    )}
                footer={
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultClosePopoverButton
                            text={applyButtonText}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => {
                                const minSelectionRequired = props.minSelectionRequired || 0;
                                const currentSelectionCount = Array.isArray(selectedValues) ? selectedValues.length : 0;

                                if (minSelectionRequired > 0 && currentSelectionCount < minSelectionRequired) {
                                    const errorMessage = props.validationErrorMessage || `At least ${minSelectionRequired} value${minSelectionRequired > 1 ? 's' : ''} need${minSelectionRequired > 1 ? '' : 's'} to be selected`;
                                    setValidationError(errorMessage);
                                    return false;
                                }
                                setValidationError('');
                                props.onChange(selectedValues);
                                return true;
                            }}
                        />
                        <DefaultClosePopoverButton
                            text={resetButtonText}
                            onClick={()=> {
                                setValidationError('');
                                if (props?.onCancel) {
                                    props?.onCancel();
                                }
                            }}
                        />
                    </div>
                }
                position='left'
                align='start'
                onClose={() => {
                    // when closing popup - reset search criteria and make sure that selected value is reset to one, supplied by props
                    // by the moment we close popup it shall be either explicitly applied with `apply` button or we revert to original values
                    setSelectedValues(props.value);
                    setFilterCriteria('');
                    setValidationError('');
                    props.filterHandler?.('');
                    if (props?.onClose) {
                        props?.onClose();

                    }
                }}
                onClickOutside={()=> {
                    if (props?.onClickOutside) {
                        props?.onClickOutside();

                    }
                }}
            >
                <ComponentWithForwardedRef {...props }/>
            </CommonPopover>
            /* eslint-enable @typescript-eslint/no-explicit-any */
        );
    };
};

export { makeMultiSelectInPopover, IMultiSelectClosedPropsDerivedFromItemsList, MultiSelectFilterType };
